import { useEffect, useRef, useState } from 'react';
import type { PropertyMedia } from '@/lib/property-media';

export function useMediaPreference() {
  const [preference, setPreference] = useState<{mobile: boolean; reduced: boolean} | null>(null);
  useEffect(() => {
    const mobile = matchMedia('(max-width: 767px)');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPreference({mobile: mobile.matches, reduced: reduced.matches});
    update(); mobile.addEventListener('change', update); reduced.addEventListener('change', update);
    return () => {mobile.removeEventListener('change', update); reduced.removeEventListener('change', update);};
  }, []);
  return preference;
}

export function PropertyPicture({media,alt,className='',priority=false}: {media:PropertyMedia;alt:string;className?:string;priority?:boolean}) {
  return <picture>
    <source media="(max-width: 767px)" srcSet={media.mobileImage} width="720" height="960" />
    <img src={media.desktopImage} alt={alt} width="1600" height="900" loading={priority?'eager':'lazy'} fetchPriority={priority?'high':'auto'} decoding="async" className={className} />
  </picture>;
}

export function PropertyMediaHero({media,title,location}: {media:PropertyMedia;title:string;location:string}) {
  const preference = useMediaPreference();
  const video = useRef<HTMLVideoElement>(null);
  const [playing,setPlaying] = useState(false);
  const [failed,setFailed] = useState(false);
  const src = preference?.mobile ? media.mobileVideo : media.desktopVideo;
  useEffect(() => {setPlaying(false);setFailed(false);}, [src]);
  return <section data-kualoa={media.desktopVideo?.includes("kualoa") || undefined} data-caldwell={media.desktopVideo?.includes("caldwell-oct15") || undefined} data-hero-header className="property-media-hero relative mt-0 aspect-[9/16] md:mt-0 md:aspect-auto md:min-h-[420px] md:max-h-[820px] overflow-hidden bg-ink md:h-[80svh]" aria-label={`${title} in pictures`}>
    <PropertyPicture media={media} alt={media.description} priority className="absolute inset-0 h-full w-full object-cover" />
    {preference && src && !preference.reduced && !failed && <video key={src} ref={video} src={src}
      poster={preference.mobile?media.mobileImage:media.desktopImage} autoPlay loop muted playsInline preload="metadata"
      onPlaying={()=>setPlaying(true)} onError={()=>{setFailed(true);setPlaying(false);}}
      aria-hidden="true" className={`absolute inset-0 h-full w-full object-cover ${playing?'opacity-100':'opacity-0'}`} />}

    <div className="absolute inset-x-0 bottom-0 px-5 pb-7 pt-16 md:px-12 md:pb-10 property-hero-copy">
      <p className="wordmark-gold mb-2 font-serif italic text-lg">{location}</p>
      <h1 data-testid="property-title" className="wordmark-gold max-w-3xl font-serif font-[500] text-[clamp(36px,5vw,64px)] leading-[0.95] tracking-[0.01em]">{title}</h1>
      <div className="mt-4 flex flex-wrap gap-3 text-sm text-ink">
        <a href="#photos" className="inline-flex min-h-11 items-center rounded border border-sand-deep bg-shell px-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">Explore photos</a>
        <a href="#photos" onClick={()=>window.dispatchEvent(new Event("property-gallery:video"))} className="inline-flex min-h-11 items-center rounded border border-sand-deep bg-shell px-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">Watch video first</a>
      </div>
    </div>

  </section>;
}
